import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { BellOff } from 'lucide-react';
import { theme } from '@/lib/theme';
import type { DormantCustomer } from '@/hooks/useDashboardData';

interface Props {
  customers: DormantCustomer[];
  loading: boolean;
}

function getDormancyColor(days: number): string {
  if (days >= 90) return '#f87171';   // rojo — más de 3 meses
  if (days >= 45) return '#fb923c';   // naranja — más de 45 días
  return '#facc15';                    // amarillo — más de 2 semanas
}

function formatDays(days: number): string {
  if (days >= 365) {
    const y = Math.floor(days / 365);
    return `${y} ${y === 1 ? 'año' : 'años'}`;
  }
  if (days >= 30) {
    const m = Math.floor(days / 30);
    return `${m} ${m === 1 ? 'mes' : 'meses'}`;
  }
  return `${days} ${days === 1 ? 'día' : 'días'}`;
}

export default function DormantCustomersWidget({ customers, loading }: Props) {
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.38, ease: 'easeOut', delay: 0.1 }}
      className="rounded-xl p-5 border flex flex-col"
      style={{ backgroundColor: theme.colors.bgSurface, borderColor: theme.colors.border }}
    >
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-white text-sm font-semibold" style={{ fontFamily: theme.fonts.display }}>
          Sin visitar
        </h4>
        <div className="w-6 h-6 flex items-center justify-center">
          <BellOff size={14} color="#fb923c" />
        </div>
      </div>

      {loading && (
        <div className="space-y-3 animate-pulse flex-1">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="flex-1 h-8 bg-white/8 rounded-lg" />
              <div className="w-14 h-4 bg-white/8 rounded" />
            </div>
          ))}
        </div>
      )}

      {!loading && customers.length === 0 && (
        <div className="flex-1 flex items-center justify-center py-4">
          <p className="text-sm" style={{ color: theme.colors.textDisabled }}>
            Sin datos aún
          </p>
        </div>
      )}

      {!loading && customers.length > 0 && (
        <div className="space-y-2 flex-1">
          {customers.map((c, i) => {
            const color = getDormancyColor(c.days_since);
            return (
              <motion.button
                key={c.id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.06 + 0.15 }}
                onClick={() => navigate('/admin/clientes')}
                className="w-full flex items-center gap-3 rounded-lg px-3 py-2 cursor-pointer text-left transition-colors"
                style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(255,255,255,0.07)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(255,255,255,0.03)';
                }}
              >
                <div
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: color }}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-white/85 text-xs font-medium truncate">{c.name}</p>
                  <p className="text-xs truncate" style={{ color: theme.colors.textDisabled }}>
                    {c.total_appointments} {c.total_appointments === 1 ? 'cita' : 'citas'} en total
                  </p>
                </div>
                <span
                  className="text-xs font-semibold whitespace-nowrap flex-shrink-0"
                  style={{ color }}
                >
                  {formatDays(c.days_since)}
                </span>
              </motion.button>
            );
          })}
        </div>
      )}

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
          <span className="text-xs" style={{ color: theme.colors.textMuted }}>+2 sem</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-orange-400" />
          <span className="text-xs" style={{ color: theme.colors.textMuted }}>+45 días</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
          <span className="text-xs" style={{ color: theme.colors.textMuted }}>+3 meses</span>
        </div>
      </div>
    </motion.div>
  );
}
